/**
 * WeHonor Logomark
 *
 * SVG mark only (no wordmark text). Cropped tight to the content bounds.
 * The original SVG has a 1536×1024 canvas; the actual mark lives at roughly
 * x=349–1189, y=219–713 → viewBox "349 219 840 494".
 */

import Svg, { G, Path } from "react-native-svg";

interface LogomarkProps {
  /** Height in pixels — width scales automatically at ~1.70 aspect ratio */
  size?: number;
  /** Primary dark fill (defaults to forest green #50694e) */
  color?: string;
  /** Lighter accent fill (defaults to #778d70) */
  accentColor?: string;
}

export function WeHonorLogomark({
  size = 32,
  color = "#29453a",
  accentColor = "#1a2e26",
}: LogomarkProps) {
  const width = Math.round(size * (840 / 494));

  return (
    <Svg
      width={width}
      height={size}
      viewBox="349 219 840 494"
    >
      {/* Primary fill paths */}
      <G>
        <Path
          fill={color}
          d="M 444.64 239.48 C 481.01 223.73 522.12 219.92 561.00 227.00 C 582.21 230.37 602.56 237.88 621.88 247.11 C 637.54 255.15 652.31 264.92 666.06 275.89 C 693.11 298.32 715.57 326.35 731.00 357.96 C 740.12 375.59 746.03 394.66 750.69 413.89 C 752.69 424.22 754.95 434.59 755.49 445.12 C 750.61 442.82 745.23 442.06 740.00 441.00 C 731.70 439.49 723.37 438.03 714.96 437.33 C 701.67 435.88 688.28 436.43 674.95 436.60 C 637.13 438.91 599.74 449.35 566.38 467.38 C 535.95 484.05 509.20 508.29 491.69 538.41 C 453.92 527.58 418.29 507.50 391.74 478.26 C 380.17 465.63 370.55 451.21 363.31 435.70 C 353.30 414.69 348.89 391.18 349.75 367.98 C 350.81 348.77 356.09 329.88 364.64 312.67 C 371.38 300.24 379.40 288.38 389.32 278.26 C 404.68 261.52 424.06 248.90 444.64 239.48 Z"
        />
        <Path
          fill={color}
          d="M 959.03 230.68 C 988.99 223.15 1020.61 221.26 1051.03 227.20 C 1094.63 234.86 1135.74 259.16 1161.40 295.55 C 1174.94 314.70 1183.63 337.26 1186.13 360.60 C 1188.19 382.06 1185.67 404.05 1178.28 424.34 C 1165.15 460.21 1138.53 490.07 1106.78 510.77 C 1088.14 523.23 1067.31 532.05 1045.89 538.45 C 1043.00 538.27 1041.99 535.42 1040.67 533.33 C 1028.82 513.27 1012.20 496.29 993.39 482.67 C 966.92 462.71 935.73 449.40 903.38 442.51 C 867.45 434.99 830.11 434.95 793.95 440.93 C 789.72 441.54 785.54 442.42 781.43 443.59 C 784.60 407.66 796.54 372.75 814.61 341.61 C 821.84 330.09 829.34 318.68 838.34 308.44 C 844.26 300.74 851.55 294.28 858.21 287.25 C 886.47 260.48 921.32 240.57 959.03 230.68 Z"
        />
        <Path
          fill={color}
          d="M 762.23 226.20 C 772.89 224.53 784.04 227.28 793.23 232.81 C 806.73 240.89 815.18 256.32 815.39 271.98 C 814.93 279.55 813.60 287.28 809.87 293.99 C 803.43 305.48 792.22 314.65 779.15 317.24 C 766.19 320.42 752.05 317.44 741.01 310.10 C 724.51 298.66 717.10 275.83 724.15 256.95 C 729.73 240.62 745.15 228.37 762.23 226.20 Z"
        />
        <Path
          fill={color}
          d="M 743.79 466.83 C 748.52 460.92 752.26 454.29 757.14 448.49 C 762.07 488.92 760.90 530.87 747.46 569.64 C 737.40 601.10 719.34 629.75 696.49 653.48 C 672.82 678.00 642.82 696.43 610.09 705.99 C 596.66 709.48 582.87 711.98 568.96 711.78 C 552.12 712.27 534.94 710.10 519.39 703.37 C 503.09 696.55 488.25 685.25 479.13 669.93 C 473.43 661.19 469.74 651.23 468.08 640.94 C 464.47 616.55 471.04 591.76 482.10 570.09 C 487.43 560.09 492.69 549.96 499.82 541.09 C 515.63 544.78 531.77 547.09 547.97 548.16 C 579.34 549.53 610.94 545.44 640.87 535.93 C 655.60 531.15 669.73 524.66 683.37 517.38 C 706.15 504.10 727.16 487.38 743.79 466.83 Z"
        />
        <Path
          fill={color}
          d="M 780.50 448.75 C 785.69 456.39 790.63 464.25 796.81 471.15 C 803.90 478.43 810.52 486.22 818.61 492.46 C 856.70 526.06 906.74 544.35 957.07 547.93 C 983.43 549.45 1010.11 547.51 1035.73 540.91 C 1053.66 564.72 1067.67 592.74 1069.29 622.95 C 1070.08 644.76 1062.90 667.55 1047.12 683.06 C 1033.45 697.33 1014.77 706.27 995.44 709.71 C 986.08 711.53 976.54 712.10 967.03 711.81 C 938.09 711.81 909.73 702.52 884.78 688.23 C 862.40 675.61 842.79 658.36 826.74 638.35 C 805.02 611.23 790.07 578.87 782.89 544.90 C 776.19 516.14 776.09 486.25 779.10 457.01 C 779.55 454.25 780.02 451.50 780.50 448.75 Z"
        />
      </G>
      {/* Accent / highlight paths */}
      <G>
        <Path
          fill={accentColor}
          d="M 566.38 467.38 C 599.74 449.35 637.13 438.91 674.95 436.60 C 688.28 436.43 701.67 435.88 714.96 437.33 C 723.37 438.03 731.70 439.49 740.00 441.00 C 745.23 442.06 750.61 442.82 755.49 445.12 C 755.91 445.32 756.75 445.71 757.17 445.90 C 757.16 446.55 757.15 447.85 757.14 448.49 C 752.26 454.29 748.52 460.92 743.79 466.83 C 727.16 487.38 706.15 504.10 683.37 517.38 C 669.73 524.66 655.60 531.15 640.87 535.93 C 610.94 545.44 579.34 549.53 547.97 548.16 C 531.77 547.09 515.63 544.78 499.82 541.09 C 499.62 540.85 499.24 540.38 499.04 540.15 C 496.59 539.53 494.13 539.02 491.69 538.41 C 509.20 508.29 535.95 484.05 566.38 467.38 Z"
        />
        <Path
          fill={accentColor}
          d="M 793.95 440.93 C 830.11 434.95 867.45 434.99 903.38 442.51 C 935.73 449.40 966.92 462.71 993.39 482.67 C 1012.20 496.29 1028.82 513.27 1040.67 533.33 C 1041.99 535.42 1043.00 538.27 1045.89 538.45 C 1042.53 539.41 1039.11 540.08 1035.73 540.91 C 1010.11 547.51 983.43 549.45 957.07 547.93 C 906.74 544.35 856.70 526.06 818.61 492.46 C 810.52 486.22 803.90 478.43 796.81 471.15 C 790.63 464.25 785.69 456.39 780.50 448.75 C 780.02 451.50 779.55 454.25 779.10 457.01 C 779.45 452.47 779.12 447.69 781.43 443.59 C 785.54 442.42 789.72 441.54 793.95 440.93 Z"
        />
      </G>
    </Svg>
  );
}
